import React, { Fragment, useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { gql, useQuery, useMutation } from "urql";
import { Link } from "wouter";
import { cloneDeep } from "lodash";
import stableStringify from "json-stable-stringify";

import { YesNoModal } from "../../../components/src/YesNoModal";
import { SubmitButton, TextField, SelectField, DateField, NumberField, TransactionField } from "../../../components/src/form";
import { useAlertTimeOut, usePrevious } from "../../../components/src/common/Utils";
import { Alert, AlertVariant } from "../../../components/src/Alert";
import {
    InstrumentModelTypeEnum,
    MatchingCoacsStatus,
    SourceType,
    CorporateActionStatusEnum,
    TransactionStatus,
    CorporateAction,
    CorporateActionStatusEnumDescriptions,
    TransactionType
} from "../types.generated";

const GET_CORPORATE_ACTIONS = gql`
    query corporateActions($filter: CorporateActionFilterInput) {
        corporateActions(filter: $filter) {
            _id
            clientId
            client {
                _id
                name
            }
            recordDate
            exDate
            paymentDate
            type
            instrument {
                _id
                name
                isin
            }
            currency
            amount
            foreignTax
            messageType
            corporateActionEvent
            status
            text
            externalId
            error
            correspondingTransactionId
            updateTimestamp
            reportIds
            swiftId
            name
        }
    }
`;

const GET_DATA = gql`
    query miniTransactions($filter: TransactionFilterInput) {
        miniTransactions(filter: $filter) {
            _id
            externalId
            brokerId
            brokerTradeId
            source
            status
            instrument {
                modelType
            }
        }
    }
`;

const MATCH_CORPORATE_ACTIONS = gql`
    mutation MatchCoacsTransactions($input: [MatchCoacsTransactionsInput!]!) {
        matchCoacsTransactions(input: $input) {
            corporateAction {
                _id
                clientId
                client {
                    _id
                    name
                }
                recordDate
                exDate
                paymentDate
                type
                instrument {
                    _id
                    name
                    isin
                }
                currency
                amount
                foreignTax
                messageType
                corporateActionEvent
                status
                text
                externalId
                error
                correspondingTransactionId
                updateTimestamp
                reportIds
                swiftId
                name
            }
        }
    }
`;

const UPDATE_CORPORATE_ACTIONS = gql`
    mutation UpsertCorporateActions($input: [CorporateActionInput!]!) {
        upsertCorporateActions(input: $input) {
            _id
            clientId
            client {
                _id
                name
            }
            recordDate
            exDate
            paymentDate
            type
            instrument {
                _id
                name
                isin
            }
            currency
            amount
            foreignTax
            messageType
            corporateActionEvent
            status
            text
            externalId
            error
            correspondingTransactionId
            updateTimestamp
            reportIds
            swiftId
            name
        }
    }
`;

type CorporateActionFormPropsType = {
    id?: string;
    tradeDate: string;
    refetch?: () => void;
};

export function CorporateActionForm(props: CorporateActionFormPropsType): React.ReactElement {
    const previousProps = usePrevious(props);
    const id = props.id ? props.id : "000000000000000000000000";

    const [{ fetching: getCorporateActionLoading, error: getCorporateActionError, data: getCorporateActionData }, refetch] = useQuery({
        query: GET_CORPORATE_ACTIONS,
        variables: {
            filter: {
                idIn: [id],
                statusIn: [
                    CorporateActionStatusEnum.Pending,
                    CorporateActionStatusEnum.Preliminary,
                    CorporateActionStatusEnum.Confirmed,
                    CorporateActionStatusEnum.UpdatedPrevious,
                    CorporateActionStatusEnum.Waiting,
                    CorporateActionStatusEnum.Settled
                ]
            }
        },
        requestPolicy: "network-only"
    });

    const [{ fetching: loading, error: getDataError, data }] = useQuery({
        query: GET_DATA,
        variables: {
            filter: {
                statusNotIn: [TransactionStatus.Deleted],
                startDate: props.tradeDate,
                sourceIn: [SourceType.Coacs, SourceType.Internal],
                typesIn: [TransactionType.Dividend, TransactionType.Interest]
            }
        },
        requestPolicy: "network-only"
    });

    const [values, setValues] = useState(null);
    const [modal, setModal] = useState({ showModal: false, payload: null });

    const [alert, setAlert] = useState<{ color: AlertVariant; visible: boolean; message: string }>({ color: "info", visible: false, message: "" });
    const onDismissAlert = () => setAlert({ color: "info", visible: false, message: "" });

    const [__stateUpdate, updateCorporateActions] = useMutation(UPDATE_CORPORATE_ACTIONS);
    const [__stateMatch, matchCorporateActions] = useMutation(MATCH_CORPORATE_ACTIONS);

    useAlertTimeOut(alert, setAlert, 5);

    useEffect(() => {
        if (stableStringify(props) !== stableStringify(previousProps)) {
            setValues(null);
        } else if (!getCorporateActionLoading && !getCorporateActionError) {
            if (values === null && getCorporateActionData.corporateActions && getCorporateActionData.corporateActions.length) {
                const inputValues = cloneDeep(getCorporateActionData.corporateActions[0]);
                inputValues.clientName = inputValues.client ? inputValues.client.name : null;
                inputValues.instrumentName = inputValues.instrument ? inputValues.instrument.name : null;
                setValues(inputValues);
            }
        }
    }, [props, previousProps, values, getCorporateActionData, getCorporateActionError, getCorporateActionLoading]);

    if (getCorporateActionError) return <div>{"Error: " + getCorporateActionError.message}</div>;
    if (getDataError) return <div>{"Error: " + getDataError.message}</div>;
    if (getCorporateActionLoading || loading) return <div className="miniform-loading">Loading</div>;
    if (!values) return <div></div>;

    const miniTransactions = cloneDeep(data.miniTransactions);
    const corporateActionsTransactions = [{ brokerTradeId: null, name: "No match", source: "", _id: "" }];
    const modelTypes = [InstrumentModelTypeEnum.Stock, InstrumentModelTypeEnum.Bond];
    miniTransactions.forEach((transaction) => {
        transaction["name"] = transaction.name ? transaction["name"] : transaction["externalId"];
        delete transaction["externalId"];
        // can only match with transactions that have not been matched
        if (
            transaction.instrument &&
            modelTypes.includes(transaction.instrument.modelType) &&
            ((!transaction.brokerTradeId && transaction.status === TransactionStatus.Pending) ||
                (values.correspondingTransactionId && transaction._id.toString() === values.correspondingTransactionId.toString()))
        ) {
            corporateActionsTransactions.push(transaction);
        }
    });

    return (
        <div className="text-start">
            {modal.showModal ? (
                <YesNoModal
                    warningText={"Are you sure you want to delete corporate action with id " + modal.payload._id + "?"}
                    modal={{
                        showModal: modal.showModal,
                        payload: modal.payload
                    }}
                    setModal={setModal}
                    onYes={() => {
                        let updatedValues: CorporateAction & { clientName: string; instrumentName: string };
                        updateCorporateActions({
                            input: [
                                {
                                    _id: modal.payload._id,
                                    status: CorporateActionStatusEnum.Deleted,
                                    error: modal.payload.error,
                                    clientId: modal.payload.clientId
                                }
                            ]
                        })
                            .then((result) => {
                                if (result.error) {
                                    setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                } else {
                                    updatedValues = result.data.upsertCorporateActions[0];
                                    setAlert({
                                        color: "success",
                                        visible: true,
                                        message: `The corporate action with id '${modal.payload._id}' was deleted successfully!`
                                    });
                                }
                            })
                            .catch((error) => {
                                console.error(error.toString());
                            })
                            .finally(() => {
                                if (updatedValues) {
                                    refetch();
                                    updatedValues.clientName = updatedValues.client ? updatedValues.client.name : null;
                                    updatedValues.instrumentName = updatedValues.instrument ? updatedValues.instrument.name : null;
                                    setValues(updatedValues);
                                    if (props.refetch) {
                                        props.refetch();
                                    }
                                }
                            });
                    }}
                />
            ) : null}

            <div id="corporateactionform" className="corporateactionform form">
                <Formik
                    enableReinitialize={true}
                    initialValues={values}
                    onSubmit={async (submitValues, { setSubmitting }) => {
                        //console.log("submitValues: ", submitValues);
                        const input = { ...submitValues };
                        let updatedValues: CorporateAction & { clientName: string; instrumentName: string } = null;
                        if (input._id !== null) {
                            if (
                                input.correspondingTransactionId &&
                                values.correspondingTransactionId !== input.correspondingTransactionId
                            ) {
                                await matchCorporateActions({
                                    input: [
                                        {
                                            corporateActionId: values._id,
                                            corporateActionExternalId: values.externalId,
                                            corporateActionError: "Manually matched",
                                            transactionId: input.correspondingTransactionId,
                                            transactionError: "Manually matched",
                                            matchingStatus: MatchingCoacsStatus.Confirmed
                                        }
                                    ]
                                })
                                    .then((result) => {
                                        if (result.error) {
                                            setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                        } else {
                                            updatedValues = result.data.matchCoacsTransactions.corporateAction;
                                            setAlert({
                                                color: "success",
                                                visible: true,
                                                message: `Corporate action '${updatedValues._id}' was updated successfully!`
                                            });
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(error.toString());
                                    })
                                    .finally(() => {
                                        setSubmitting(false);
                                        if (updatedValues) {
                                            refetch();
                                            updatedValues.clientName = updatedValues.client ? updatedValues.client.name : null;
                                            updatedValues.instrumentName = updatedValues.instrument ? updatedValues.instrument.name : null;
                                            setValues(updatedValues);
                                            if (props.refetch) {
                                                props.refetch();
                                            }
                                        }
                                    });
                            } else if (!input.correspondingTransactionId && values.correspondingTransactionId) {
                                matchCorporateActions({
                                    input: [
                                        {
                                            corporateActionId: values._id,
                                            corporateActionExternalId: values.externalId,
                                            corporateActionError: null,
                                            transactionId: values.correspondingTransactionId,
                                            transactionError: null,
                                            matchingStatus: MatchingCoacsStatus.Mismatch
                                        }
                                    ]
                                })
                                    .then((result) => {
                                        if (result.error) {
                                            setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                        } else {
                                            updatedValues = result.data.matchCoacsTransactions.corporateAction;
                                            setAlert({
                                                color: "success",
                                                visible: true,
                                                message: `Corporate action '${updatedValues._id}' was updated successfully!`
                                            });
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(error.toString());
                                    })
                                    .finally(() => {
                                        setSubmitting(false);
                                        if (updatedValues) {
                                            refetch();
                                            updatedValues.clientName = updatedValues.client ? updatedValues.client.name : null;
                                            updatedValues.instrumentName = updatedValues.instrument ? updatedValues.instrument.name : null;
                                            setValues(updatedValues);
                                            if (props.refetch) {
                                                props.refetch();
                                            }
                                        }
                                    });
                            } else {
                                if (input.status && input.status === CorporateActionStatusEnum.Deleted) {
                                    setModal({ showModal: true, payload: input });
                                } else {
                                    updateCorporateActions({
                                        input: [{ _id: input._id, status: input.status, error: input.error, clientId: input.clientId }]
                                    })
                                        .then((result) => {
                                            if (result.error) {
                                                setAlert({ color: "danger", visible: true, message: result.error.toString() });
                                            } else {
                                                updatedValues = result.data.upsertCorporateActions[0];
                                                setAlert({
                                                    color: "success",
                                                    visible: true,
                                                    message: `Corporate action '${updatedValues._id}' was updated successfully!`
                                                });
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error.toString());
                                        })
                                        .finally(() => {
                                            setSubmitting(false);
                                            if (updatedValues) {
                                                refetch();
                                                updatedValues.clientName = updatedValues.client ? updatedValues.client.name : null;
                                                updatedValues.instrumentName = updatedValues.instrument
                                                    ? updatedValues.instrument.name
                                                    : null;
                                                setValues(updatedValues);
                                                if (props.refetch) {
                                                    props.refetch();
                                                }
                                            }
                                        });
                                }
                            }
                        }
                        setSubmitting(false);
                    }}
                >
                    {({ isSubmitting, values, errors }) => {
                        if (Object.keys(errors).length > 0) {
                            console.log(errors);
                        }
                        return (
                            <Fragment>
                                <div className="flex flex-wrap -mx-2">
                                    <div>
                                        <Form autoComplete="off">
                                            <div className="flex flex-wrap -mx-2">
                                                <TextField
                                                    name="clientName"
                                                    label={<Link to={"/parties/" + values.clientId}>Client</Link>}
                                                    className="w-full px-2 mb-4"
                                                    disabled={true}
                                                />
                                            </div>
                                            <div className="flex flex-wrap -mx-2">
                                                <TextField
                                                    name="corporateActionEvent"
                                                    label="Type"
                                                    className="w-full md:w-4/12 px-2 mb-4"
                                                    disabled={true}
                                                />
                                                <TextField
                                                    name="instrumentName"
                                                    label={
                                                        values.instrument ? (
                                                            <Link to={"/instruments/" + values.instrument._id}>Instrument</Link>
                                                        ) : (
                                                            "Instrument"
                                                        )
                                                    }
                                                    className="w-full md:w-8/12 px-2 mb-4"
                                                    disabled={true}
                                                />
                                            </div>
                                            <div className="flex flex-wrap -mx-2">
                                                <DateField
                                                    name="exDate"
                                                    label="Trade date"
                                                    className="w-full md:w-4/12 px-2 mb-4"
                                                    disabled={true}
                                                />
                                                <DateField
                                                    name="paymentDate"
                                                    label="Value date"
                                                    className="w-full md:w-4/12 px-2 mb-4"
                                                    disabled={true}
                                                />
                                                <TextField
                                                    name="currency"
                                                    label="Currency"
                                                    className="w-full md:w-4/12 px-2 mb-4"
                                                    disabled={true}
                                                />
                                            </div>
                                            <div className="flex flex-wrap -mx-2">
                                                <NumberField
                                                    name="foreignTax"
                                                    label="Foreign tax"
                                                    className="w-full md:w-1/2 px-2 mb-4"
                                                    disabled={true}
                                                />
                                                <NumberField
                                                    name="amount"
                                                    label="Amount"
                                                    className="w-full md:w-1/2 px-2 mb-4"
                                                    disabled={true}
                                                />
                                            </div>
                                            <div className="flex flex-wrap -mx-2">
                                                <TransactionField
                                                    name="correspondingTransactionId"
                                                    label={
                                                        values.correspondingTransactionId ? (
                                                            <a href={"/transaction/" + values.correspondingTransactionId} target="_blank" rel="noopener noreferrer">
                                                                Matched with
                                                            </a>
                                                        ) : (
                                                            <div>Matched with</div>
                                                        )
                                                    }
                                                    className="w-full px-2 mb-4"
                                                    options={corporateActionsTransactions}
                                                    disabled={false}
                                                />
                                            </div>
                                            <div className="flex flex-wrap -mx-2">
                                                <SelectField
                                                    name="status"
                                                    label="Status"
                                                    className="w-full md:w-1/2 px-2 mb-4"
                                                    options={Object.keys(CorporateActionStatusEnum)}
                                                    tooltips={CorporateActionStatusEnumDescriptions}
                                                    disabled={false}
                                                />
                                                <TextField
                                                    name="externalId"
                                                    label={"External id"}
                                                    type="text"
                                                    className="w-full md:w-1/2 px-2 mb-4"
                                                    disabled={true}
                                                />
                                            </div>

                                            <div className="flex flex-wrap -mx-2">
                                                <TextField
                                                    name="error"
                                                    label="Error"
                                                    className="w-full md:w-2/3 px-2 mb-4"
                                                    disabled={values._id ? false : true}
                                                />

                                                <TextField
                                                    name="_id"
                                                    label="Id"
                                                    type="text"
                                                    className="w-full md:w-1/3 px-2 mb-4"
                                                    disabled={true}
                                                />
                                            </div>
                                            {values.swiftId ? (
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full px-2 mb-4">
                                                        <a className="" href={`/swiftcorporateaction/` + values.swiftId} target="_blank" rel="noopener noreferrer">
                                                            Swift
                                                        </a>
                                                    </div>
                                                </div>
                                            ) : null}
                                            {values.reportIds && values.reportIds.length === 1 ? (
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full px-2 mb-4">
                                                        <a className="" href={`/report/` + values.reportIds[0]} target="_blank" rel="noopener noreferrer">
                                                            Report
                                                        </a>
                                                    </div>
                                                </div>
                                            ) : values.reportIds && values.reportIds.length > 1 ? (
                                                <div className="flex flex-wrap -mx-2">
                                                    <div className="w-full px-2 mb-4">
                                                        <Link
                                                            className=""
                                                            target="_blank"
                                                            to={`/reports/?clientId=` + values.clientId + `&type=CorporateActions`}
                                                        >
                                                            Reports
                                                        </Link>
                                                    </div>
                                                </div>
                                            ) : null}
                                            <SubmitButton disabled={isSubmitting || Object.keys(errors).length > 0} label={"Save"} />
                                            {alert.visible ? (
                                                <Alert variant={alert.color} onClose={onDismissAlert} dismissible className="mt-2">
                                                    {alert.message}
                                                </Alert>
                                            ) : null}
                                        </Form>
                                    </div>
                                </div>
                            </Fragment>
                        );
                    }}
                </Formik>
            </div>
        </div>
    );
}
